import {usePortuna} from '@/composables/portuna';
import Isset from '@/library/utils';
import {computed, type Ref, ref} from 'vue';
import {useStore} from 'vuex';

export function useAbstractFormInput(
    parentFormRow: Optional<IFormRowContext>,
    id: Ref<Optional<string>>,
    required: Ref<boolean>,
    overwriteRequired: Optional<Ref<boolean>> = undefined,
    errorName: Optional<Ref<string>>          = undefined,
    filterName: Optional<Ref<string>>         = undefined,
)
{
    const portuna = usePortuna();

    if (required && !Isset(required.value))
    {
        required.value = false;
    }

    if (overwriteRequired && !Isset(overwriteRequired.value))
    {
        overwriteRequired.value = false;
    }

    if (errorName && !Isset(errorName.value))
    {
        errorName.value = '';
    }

    if (filterName && !Isset(filterName.value))
    {
        filterName.value = '';
    }

    const store = useStore();

    const isFormField = ref<boolean>(true);

    const formErrors = computed<Record<string, string[]>>(() => store.state.form.errors);

    const formSubmitted = computed<boolean>(() => store.state.form.submitted);

    const formValidated = computed<FormStateValidated>(() => store.state.form.validated);

    const triggerFocus = (): void =>
    {
        portuna.root?.value?.focus();
    };

    const _id = computed(() =>
    {
        return id.value! || parentFormRow?.labelFor || null;
    });

    const _required = computed(() =>
    {
        return required.value! || ((overwriteRequired && !overwriteRequired.value!) && parentFormRow?._required?.value) || false;
    });

    const isInvalid = computed(() =>
    {
        const proxyErrorName = errorName ? errorName.value! : (parentFormRow?._errorName?.value || '');
        if (!proxyErrorName)
        {
            return null;
        }

        if (!formSubmitted.value && !formValidated.value[proxyErrorName])
        {
            return null;
        }

        return typeof formErrors.value[proxyErrorName] !== 'undefined';
    });

    const ariaValidationAttributes = computed(() =>
    {
        if (!parentFormRow || parentFormRow.isInvalid?.value)
        {
            return {};
        }

        return {
            'aria-invalid': true,
            'aria-describedby': parentFormRow.formErrorId?.value,
        };
    });

    const validateValue = (newValue: Optional<InputValueType>, context: Record<string, any> = {}): Promise<any> =>
    {
        if (!errorName || !filterName)
        {
            return Promise.resolve();
        }

        const proxyErrorName  = errorName.value! || parentFormRow?._errorName?.value || '';
        const proxyFilterName = filterName.value! || parentFormRow?._filterName?.value || proxyErrorName;

        if (proxyErrorName && proxyFilterName)
        {
            // TODO this creates problems if there are backend errors but no frontend errors (backend errors are deleted, but form is still invalid)
            // TODO see formsetps in enrolment (empty url in instagram social share network entry)
            store.commit('form/resetErrors', proxyErrorName);

            return store.dispatch('form/validate', {
                value: newValue,
                context: context,
                errorName: proxyErrorName,
                filterName: proxyFilterName,
            });
        }

        return Promise.resolve();
    };

    const returnValues = {
        isFormField,
        formErrors,
        formSubmitted,
        formValidated,
        triggerFocus,
        // Compatibility
        focus: triggerFocus,
        _id,
        _required,
        isInvalid,
        ariaValidationAttributes,
        validateValue
    };

    return {
        expose: returnValues,
        ...returnValues,
    };
}